<template>
  <BaseModal :modalTitle="$t('area.tat.create.title')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputSelectVee
        name="type"
        :label="$t('term.type')"
        :options="typeOptions"
        :rules="['required']"
        isRequired
        v-model="form.type" />
      <template v-if="isFixedDeadline">
        <UiInputNumberFieldVee
          name="workingDays"
          :label="$t('term.working-days')"
          :min="1"
          isRequired
          showAsRequired
          v-model="form.workingDays" />
        <UiInputNumberFieldVee
          name="deadlineHour"
          :label="$t('time.hour')"
          :min="0"
          :max="23"
          isRequired
          showAsRequired
          v-model="form.deadlineHour" />
        <UiInputNumberFieldVee
          name="deadlineMinute"
          :label="$t('time.minute')"
          :min="0"
          :max="59"
          isRequired
          showAsRequired
          v-model="form.deadlineMinute" />
      </template>
      <template v-else>
        <UiInputTextFieldVee
          name="name"
          :label="$t('term.name')"
          :rules="['required']"
          isRequired
          :unit="currentLocale.toUpperCase()"
          v-model="form.name" />
        <UiInputSelectVee
          name="durationMode"
          :label="$t('W: Type af walk-in')"
          :options="durationModeOptions"
          :rules="['required']"
          isRequired
          v-model="form.durationMode" />
        <UiInputNumberFieldVee
          v-if="isHoursBasedWalkIn"
          name="durationHours"
          :label="$t('area.tat.create.duration-hours')"
          :min="1"
          isRequired
          showAsRequired
          v-model="form.durationHours" />
        <span v-else class="text-sm text-text-secondary">
          {{
            $t('W: Fristen er drop pointets lukketid den dag, prøven afleveres.')
          }}
        </span>
      </template>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import type {
  TTurnaroundTimeType,
  TWalkInDurationMode,
} from '@team-milva/lab-models/resources'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useModalStore } from '@/stores/modalStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreateTatModal',

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        type: 'fixed-deadline' as TTurnaroundTimeType,
        durationMode: 'hours' as TWalkInDurationMode,
        workingDays: null as number | null,
        deadlineHour: null as number | null,
        deadlineMinute: null as number | null,
        name: '',
        durationHours: null as number | null,
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    typeOptions() {
      return [
        { value: 'fixed-deadline', text: this.$t('tat.title.fixed-deadline') },
        { value: 'walk-in', text: this.$t('tat.title.walk-in') },
      ]
    },

    isFixedDeadline(): boolean {
      return this.form.type === 'fixed-deadline'
    },

    // A same-day walk-in states no hours — its deadline is the drop point's
    // closing time — so only an hours-based one shows and requires the field.
    isHoursBasedWalkIn(): boolean {
      return this.form.durationMode === 'hours'
    },

    durationModeOptions() {
      return [
        { value: 'hours', text: this.$t('W: Antal timer') },
        { value: 'same-day', text: this.$t('W: Samme dag') },
      ]
    },
  },

  methods: {
    ...mapActions(useTurnaroundTimeStore, ['createTAT']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit() {
      this.isLoading = true
      this.errorText = ''

      try {
        if (this.form.type === 'fixed-deadline') {
          await this.createTAT({
            type: 'fixed-deadline',
            workingDays: this.form.workingDays ?? 0,
            deadline: {
              hour: this.form.deadlineHour ?? 0,
              minute: this.form.deadlineMinute ?? 0,
            },
          })
        } else if (this.form.durationMode === 'same-day') {
          await this.createTAT({
            type: 'walk-in',
            durationMode: 'same-day',
            name: this.form.name,
          })
        } else {
          await this.createTAT({
            type: 'walk-in',
            durationMode: 'hours',
            name: this.form.name,
            durationHours: this.form.durationHours ?? 0,
          })
        }
        useToast().success(this.$t('W: Svartid oprettet'))
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.closeTop()
      } catch {
        useToast().error(this.$t('W: Kunne ikke oprette svartid'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

